import React, { useCallback, useMemo, useState } from 'react';
import { Box, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useForm, Controller } from 'react-hook-form';
import { SupplierDto } from '@vatos-pas/common';

import {
  FormToolbar,
  Autocomplete,
  AsyncFieldWrapper,
} from 'components/standalone';
import { useRefreshTrigger } from 'hooks';
import {
  SupplierFormValues,
  RegionOption,
  UserRepresentativeOption,
} from '../containers';
import { validateSupplier } from '../views/supplierValidation';
import { createValidationResolver } from 'utils';
import { PermissionsProvider } from 'providers/permissionsProvider';
import { AppRole } from 'providers/roles';

export interface SupplierFormProps {
  mode: 'create' | 'edit';
  initialValues?: SupplierDto | null;
  initialRegionIds?: string[];
  initialRepresentativeIds?: string[];
  onSubmit: (values: SupplierFormValues) => Promise<void>;
  loading: boolean;
  onGoBack: () => void;
  loadRegionOptions: (searchTerm: string) => Promise<RegionOption[]>;
  loadUserRepresentativeOptions: (
    searchTerm: string,
  ) => Promise<UserRepresentativeOption[]>;
  regionsLoading?: boolean;
  representativesLoading?: boolean;
  regionsError?: string | null;
  representativesError?: string | null;
}

const getDefaultValues = (
  initialValues?: SupplierDto | null,
  initialRegionIds: string[] = [],
  initialRepresentativeIds: string[] = [],
): SupplierFormValues => ({
  name: initialValues?.name || '',
  externalId: initialValues?.externalId || '',
  description: initialValues?.description || '',
  email: initialValues?.email || '',
  phone: initialValues?.phone || '',
  contactInfo: initialValues?.contactInfo || '',
  selectedRegions: initialRegionIds,
  selectedRepresentatives: initialRepresentativeIds,
});

export const SupplierForm: React.FC<SupplierFormProps> = ({
  mode,
  initialValues,
  initialRegionIds = [],
  initialRepresentativeIds = [],
  onSubmit,
  loading,
  onGoBack,
  loadRegionOptions,
  loadUserRepresentativeOptions,
  regionsLoading = false,
  representativesLoading = false,
  regionsError = null,
  representativesError = null,
}) => {
  const classes = useStyles();
  const isCreateMode = mode === 'create';

  const [regionOptionsCache, setRegionOptionsCache] = useState<RegionOption[]>(
    [],
  );
  const [representativeOptionsCache, setRepresentativeOptionsCache] = useState<
    UserRepresentativeOption[]
  >([]);
  const [refreshKey, setRefreshKey] = useState(0);

  useRefreshTrigger(() => {
    setRegionOptionsCache([]);
    setRepresentativeOptionsCache([]);
    setRefreshKey(k => k + 1);
  });

  const loadTrigger: 'focus' | 'mount' = isCreateMode ? 'focus' : 'mount';

  const formValues = useMemo(
    () =>
      initialValues
        ? getDefaultValues(
            initialValues,
            initialRegionIds,
            initialRepresentativeIds,
          )
        : undefined,
    [initialValues, initialRegionIds, initialRepresentativeIds],
  );

  const { hasRole } = PermissionsProvider.useContainer();
  const actorIsRegionalAdmin =
    hasRole(AppRole.RegionalAdmin) && !hasRole(AppRole.SuperAdmin);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<SupplierFormValues>({
    defaultValues: getDefaultValues(
      initialValues,
      initialRegionIds,
      initialRepresentativeIds,
    ),
    values: formValues,
    resetOptions: { keepDirtyValues: true },
    resolver: createValidationResolver<SupplierFormValues>(values =>
      validateSupplier(values, { requireRegion: actorIsRegionalAdmin }),
    ),
    mode: 'onBlur',
  });

  const onFormSubmit = useMemo(
    () => handleSubmit(onSubmit),
    [handleSubmit, onSubmit],
  );

  const handleLoadRegionOptions = useCallback(
    async (searchTerm: string): Promise<RegionOption[]> => {
      const options = await loadRegionOptions(searchTerm);
      setRegionOptionsCache(options);
      return options;
    },
    [loadRegionOptions],
  );

  const handleLoadRepresentativeOptions = useCallback(
    async (searchTerm: string): Promise<UserRepresentativeOption[]> => {
      const options = await loadUserRepresentativeOptions(searchTerm);
      setRepresentativeOptionsCache(options);
      return options;
    },
    [loadUserRepresentativeOptions],
  );

  const getRegionObjects = useCallback(
    (regionIds: string[]): RegionOption[] =>
      regionIds
        .map(id => regionOptionsCache.find(r => r.id === id))
        .filter((r): r is RegionOption => !!r),
    [regionOptionsCache],
  );

  const getRepresentativeObjects = useCallback(
    (userIds: string[]): UserRepresentativeOption[] =>
      userIds
        .map(id => representativeOptionsCache.find(r => r.id === id))
        .filter((r): r is UserRepresentativeOption => !!r),
    [representativeOptionsCache],
  );

  return (
    <Box>
      <Box className={classes.formContent}>
        <Box className={classes.fields}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Name"
                error={!!errors.name}
                helperText={errors.name?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
          <Controller
            name="externalId"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="External ID"
                error={!!errors.externalId}
                helperText={errors.externalId?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
        </Box>

        <Box className={classes.fields} my={3}>
          <Controller
            name="email"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Email"
                error={!!errors.email}
                helperText={errors.email?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Phone"
                error={!!errors.phone}
                helperText={errors.phone?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
          <Controller
            name="contactInfo"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Contact Info"
                error={!!errors.contactInfo}
                helperText={errors.contactInfo?.message}
                margin="dense"
                variant="filled"
              />
            )}
          />
        </Box>

        <Box className={classes.fields} mb={3}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                className={classes.input}
                label="Description"
                error={!!errors.description}
                helperText={errors.description?.message}
                margin="dense"
                variant="filled"
                multiline
                rows={3}
              />
            )}
          />
        </Box>

        <Box className={classes.fields} mb={1.5}>
          <Controller
            name="selectedRegions"
            control={control}
            render={({ field, fieldState: { error } }) => {
              const regionObjects = getRegionObjects(field.value || []);
              return (
                <Box className={classes.fullWidthField}>
                  <AsyncFieldWrapper loading={regionsLoading}>
                    <Autocomplete<RegionOption>
                      key={`regions-${refreshKey}`}
                      disableCloseOnSelect
                      multiple
                      clientSideFilter
                      label="Regions"
                      loadOptions={handleLoadRegionOptions}
                      loadTrigger={loadTrigger}
                      loading={regionsLoading}
                      value={regionObjects}
                      onChange={newValue => {
                        // Links the user cannot see (no loaded option) must survive every change.
                        const hidden = (field.value ?? []).filter(
                          (id: string) => !regionObjects.some(o => o.id === id),
                        );
                        field.onChange([...hidden, ...newValue.map(r => r.id)]);
                      }}
                      getOptionLabel={option => option.name}
                      getOptionValue={option => option.id}
                      error={error?.message ?? regionsError ?? undefined}
                      fullWidth
                    />
                  </AsyncFieldWrapper>
                </Box>
              );
            }}
          />
        </Box>

        {!isCreateMode && (
          <Box className={classes.fields} mb={1.5}>
            <Controller
              name="selectedRepresentatives"
              control={control}
              render={({ field, fieldState: { error } }) => {
                const representativeObjects = getRepresentativeObjects(
                  field.value || [],
                );
                return (
                  <Box className={classes.fullWidthField}>
                    <AsyncFieldWrapper loading={representativesLoading}>
                      <Autocomplete<UserRepresentativeOption>
                        key={`representatives-${refreshKey}`}
                        disableCloseOnSelect
                        multiple
                        label="Supplier Representatives"
                        loadOptions={handleLoadRepresentativeOptions}
                        loadTrigger={loadTrigger}
                        loading={representativesLoading}
                        value={representativeObjects}
                        onChange={newValue => {
                          // Links the user cannot see (no loaded option) must survive every change.
                          const hidden = (field.value ?? []).filter(
                            (id: string) =>
                              !representativeObjects.some(o => o.id === id),
                          );
                          field.onChange([
                            ...hidden,
                            ...newValue.map(r => r.id),
                          ]);
                        }}
                        getOptionLabel={option => option.name}
                        getOptionValue={option => option.id}
                        error={
                          error?.message ?? representativesError ?? undefined
                        }
                        fullWidth
                      />
                    </AsyncFieldWrapper>
                  </Box>
                );
              }}
            />
          </Box>
        )}
      </Box>

      <FormToolbar
        onGoBack={onGoBack}
        onSave={onFormSubmit}
        saveLoading={loading}
        showSave
      />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  formContent: {
    padding: theme.spacing(2),
  },
  input: {
    margin: '0px 15px',
  },
  fields: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
  },
  fullWidthField: {
    flex: 1,
    margin: '0px 15px',
  },
}));

export default SupplierForm;
